import {FC, ReactNode, useCallback, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';
import ReactCrop, {Crop} from 'react-image-crop';

import {Box, Dialog, Stack} from '@mui/material';

import 'react-image-crop/dist/ReactCrop.css';
import {EmpionButton} from '../EmpionButton/EmpionButton';
import FileUploader from '../FileUploader/FileUploader';

const FileImageUploader: FC<{
  renderTrigger: () => ReactNode;
  onImageChange?: (imageFile: File) => void;
}> = ({renderTrigger, onImageChange}) => {
  const [src, setSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>({unit: '%', width: 100, height: 100, x: 0, y: 0});
  const [openModal, setOpenModal] = useState(false);
  const [croppedImageUrl, setCroppedImageUrl] = useState<string | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const {t} = useTranslation();

  const onCropComplete = useCallback(() => {
    if (!crop) {
      return;
    }
    if (imageRef.current && crop.width && crop.height) {
      const canvas = previewCanvasRef.current;
      if (!canvas) {
        return;
      }
      const ctx = canvas.getContext('2d');
      const scaleX = imageRef.current?.naturalWidth / imageRef.current?.width;
      const scaleY = imageRef.current?.naturalHeight / imageRef.current?.height;
      canvas.width = crop.width;
      canvas.height = crop.height;
      ctx?.drawImage(
        imageRef.current,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        crop.width,
        crop.height
      );
      canvas.toBlob((blob) => {
        if (!blob) {
          return;
        }
        const croppedImageUrl = URL.createObjectURL(blob);
        setCroppedImageUrl(croppedImageUrl);
      }, 'image/jpeg');
    }
  }, [crop]);

  const handleModalClose = () => {
    setOpenModal(false);
    setCrop({unit: '%', width: 100, height: 100, x: 0, y: 0});
    setCroppedImageUrl(null);
    setSrc(null);
  };

  const handleSave = useCallback(() => {
    const imageUrlToSave = croppedImageUrl || src;
    if (!imageUrlToSave) {
      return;
    }
    onCropComplete();
    (async () => {
      handleModalClose();
      const blob = await fetch(imageUrlToSave).then((r) => r.blob());
      const imageFile = new File([blob], 'profile-picture.jpeg', {type: 'image/jpeg'});
      onImageChange && onImageChange(imageFile);
    })().then();
  }, [croppedImageUrl, src, onImageChange, onCropComplete]);

  return (
    <>
      {openModal && (
        <Dialog
          open={openModal}
          onClose={handleModalClose}
          aria-labelledby='image-crop-modal'
          aria-describedby='modal-modal-description'
        >
          <Box
            sx={{
              position: 'flex',
              minWidth: {xs: '300px', sm: '500px'},
              maxHeight: '80vh',
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 4,
              }}
            >
              <ReactCrop crop={crop} onChange={(newCrop) => setCrop(newCrop)} onComplete={onCropComplete} aspect={1}>
                <Box component='img' src={src || ''} alt='Crop me' ref={imageRef} />
                <canvas
                  ref={previewCanvasRef}
                  style={{
                    border: '1px solid black',
                    objectFit: 'contain',
                    width: '100%',
                    height: '100%',
                    display: 'none',
                  }}
                />
              </ReactCrop>
            </Box>

            <Stack
              gap={2}
              sx={{
                display: 'flex',
                flexDirection: {xs: 'column', sm: 'row'},
                justifyContent: 'space-between',
              }}
            >
              <EmpionButton variant='contained' color='error' onClick={handleModalClose}>
                {t('shared.formButtons.cancel')}
              </EmpionButton>
              <EmpionButton variant='contained' color='primary' onClick={handleSave}>
                {t('shared.formButtons.save')}
              </EmpionButton>
            </Stack>
          </Box>
        </Dialog>
      )}
      <FileUploader
        renderTrigger={renderTrigger}
        accept='image/*'
        onFileSelect={(file) => {
          setSrc(URL.createObjectURL(file));
          setOpenModal(true);
        }}
      />
    </>
  );
};

export default FileImageUploader;
